import { BookOpen, CircleUserRound, Headphones, Mail, Phone, Video } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SafeImage from '../../../shared/components/SafeImage';
import { userService } from '../../../shared/services/userService';
import { useShop } from '../../../shared/store';
import type { Product } from '../../../shared/types';
import { getProductMediaType } from '../../../shared/utils/productMedia';

type PublisherCard = {
  name: string;
  email?: string;
  phone?: string;
  altPhone?: string;
  books: Product[];
  profileUrl?: string;
  slug: string;
};

type PublisherDirectoryEntry = {
  profileUrl?: string;
  phone?: string;
};

const getInitials = (name: string): string => {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '?';
  const first = tokens[0]?.[0] ?? '';
  const last = tokens.length > 1 ? tokens[tokens.length - 1]?.[0] ?? '' : '';
  return `${first}${last}`.toUpperCase() || '?';
};

const normalizeLookupValue = (value?: string): string => value?.trim().toLowerCase() ?? '';

const slugifyPublisher = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'publisher';

const getPublisherName = (product: Product): string => {
  const name =
    product.publishedCompany?.trim() ||
    product.uploaderName?.trim() ||
    product.authorName?.trim() ||
    product.author?.trim() ||
    '';

  if (normalizeLookupValue(name) === 'publisher 1') {
    return product.uploaderName?.trim() || product.authorName?.trim() || product.author?.trim() || '';
  }

  return name;
};

const getPublisherEmail = (product: Product): string | undefined =>
  product.publisherEmail?.trim() || product.uploaderEmail?.trim();

const getPublisherPhone = (product: Product): string | undefined =>
  product.publisherPhone?.trim();

const getPublisherAltPhone = (product: Product): string | undefined =>
  product.publisherAltPhone?.trim();

const PublisherProfilePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { products } = useShop();

  const publisher = useMemo<PublisherCard | null>(() => {
    if (!slug) return null;

    const map = new Map<string, PublisherCard>();

    products.forEach((product) => {
      const publisherName = getPublisherName(product);
      if (!publisherName) return;

      const publisherSlug = slugifyPublisher(publisherName);
      if (publisherSlug !== slug) return;

      const key = normalizeLookupValue(publisherName);
      const publisherEmail = getPublisherEmail(product);
      const publisherPhone = getPublisherPhone(product);
      const publisherAltPhone = getPublisherAltPhone(product);
      const existing = map.get(key) ?? { name: publisherName, books: [], slug: publisherSlug };

      if (!existing.email && publisherEmail) existing.email = publisherEmail;
      if (!existing.phone && publisherPhone) existing.phone = publisherPhone;
      if (!existing.altPhone && publisherAltPhone) existing.altPhone = publisherAltPhone;
      if (!existing.profileUrl && product.coverUrl) existing.profileUrl = product.coverUrl;

      existing.books.push(product);
      map.set(key, existing);
    });

    return map.size > 0 ? [...map.values()][0] : null;
  }, [products, slug]);

  if (products.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#070b16] transition-colors duration-300">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-[#0b1220] p-6 text-center text-sm font-semibold text-gray-600 dark:text-slate-400 shadow-sm">
            Loading publisher...
          </div>
        </div>
      </div>
    );
  }

  if (!publisher) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#070b16] transition-colors duration-300">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-[#0b1220] p-6 text-center shadow-sm">
            <p className="text-sm text-gray-600 dark:text-slate-400">Publisher not found.</p>
            <button
              type="button"
              onClick={() => navigate('/publishers')}
              className="mt-4 rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
            >
              Back to Publishers
            </button>
          </div>
        </div>
      </div>
    );
  }

  const counts = publisher.books.reduce(
    (acc, item) => {
      const media = getProductMediaType(item);
      acc[media] += 1;
      return acc;
    },
    { book: 0, audio: 0, animation: 0 },
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#070b16] transition-colors duration-300">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:py-12">
        <button
          type="button"
          onClick={() => navigate('/publishers')}
          className="mb-4 inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-slate-800 bg-white dark:bg-[#0b1220] px-3 py-1 text-xs font-semibold text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        >
          &larr; Back to Publishers
        </button>

        <div className="rounded-3xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-[#0b1220] p-6 shadow-sm lg:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="space-y-4 lg:w-1/3">
              <div className="flex items-center gap-3">
                <SafeImage
                  src={publisher.profileUrl}
                  alt={publisher.name}
                  className="h-16 w-16 rounded-full border border-orange-100 dark:border-orange-900/30 bg-orange-50 dark:bg-orange-950/20 object-cover"
                  icon={CircleUserRound}
                  iconClassName="h-16 w-16 text-orange-600 dark:text-orange-500"
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">Content Seller Profile</p>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-200">{publisher.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-slate-500">@{publisher.name}</p>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-700 dark:text-slate-300">
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-orange-500" />
                  {publisher.email ? (
                    <a
                      href={`mailto:${publisher.email.trim()}`}
                      className="break-all whitespace-normal hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      {publisher.email.trim()}
                    </a>
                  ) : (
                    <span className="dark:text-slate-400">Email not provided</span>
                  )}
                </div>

                {publisher.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-orange-500" />
                    <a href={`tel:${publisher.phone}`} className="hover:text-orange-600 dark:hover:text-orange-400">
                      {publisher.phone}
                    </a>
                  </div>
                )}

                {publisher.altPhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-orange-500" />
                    <a href={`tel:${publisher.altPhone}`} className="hover:text-orange-600 dark:hover:text-orange-400">
                      {publisher.altPhone}
                    </a>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-center text-xs font-semibold text-gray-700 dark:text-slate-400">
                <div className="rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/50 p-3">
                  <BookOpen className="mx-auto h-4 w-4 text-orange-500" />
                  <p className="mt-1">{counts.book} books</p>
                </div>
                <div className="rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/50 p-3">
                  <Headphones className="mx-auto h-4 w-4 text-orange-500" />
                  <p className="mt-1">{counts.audio} audio</p>
                </div>
                <div className="rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/50 p-3">
                  <Video className="mx-auto h-4 w-4 text-orange-500" />
                  <p className="mt-1">{counts.animation} video</p>
                </div>
              </div>
            </div>

            <div className="lg:flex-1">
              <h4 className="mb-3 text-base font-semibold text-gray-900 dark:text-slate-200">Content</h4>
              <div className="grid gap-3 md:grid-cols-2">
                {publisher.books.map((book) => {
                  const media = getProductMediaType(book);
                  const target = `/products/${book.slug ?? book.id}`;

                  return (
                    <button
                      type="button"
                      key={book.id}
                      onClick={() => navigate(target)}
                      className="overflow-hidden rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900/40 text-left shadow-[0_10px_25px_-18px_rgba(15,23,42,0.35)] transition hover:shadow-md"
                    >
                      <div className="flex items-center gap-3 p-3">
                        <SafeImage
                          src={book.imageUrl}
                          alt={book.nameKey}
                          className="h-16 w-16 rounded-lg bg-gray-100 dark:bg-slate-800 object-cover"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-900 dark:text-slate-200">{book.nameKey}</p>
                          <p className="truncate text-xs text-gray-500 dark:text-slate-400">{book.descriptionKey}</p>
                          <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-slate-800 px-2 py-1 text-[10px] font-semibold text-gray-700 dark:text-slate-300">
                            {media === 'book' ? (
                              <BookOpen className="h-3 w-3 text-orange-500" />
                            ) : media === 'audio' ? (
                              <Headphones className="h-3 w-3 text-orange-500" />
                            ) : (
                              <Video className="h-3 w-3 text-orange-500" />
                            )}
                            {media}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublisherProfilePage;
